import { useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { authorizeCode, sendEmailMfaCode, verifyMfaChallenge, type MfaMethod } from '../lib/api';
import AuthLayout from './AuthLayout';

type Status = 'idle' | 'submitting' | 'success' | 'error';

interface SuccessTokens {
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
}

// Hosted-login flow params optionally carried through the URL when we're
// resuming a /login → /mfa/challenge round-trip. When all four are present
// we exchange the bearer issued by /verify for an authorize_code and
// redirect to the consumer; otherwise we stop at "Signed in" (the page is
// also reachable directly, e.g. for in-place MFA enrollment flows).
interface HostedLoginFlow {
  redirectUri: string;
  state: string;
  codeChallenge: string;
  codeChallengeMethod: string;
}

function readHostedLoginFlow(params: URLSearchParams): HostedLoginFlow | null {
  const redirectUri = params.get('redirect_uri') ?? '';
  const state = params.get('state') ?? '';
  const codeChallenge = params.get('code_challenge') ?? '';
  const codeChallengeMethod = params.get('code_challenge_method') ?? '';
  if (!redirectUri || !state || !codeChallenge || !codeChallengeMethod) {
    return null;
  }
  return { redirectUri, state, codeChallenge, codeChallengeMethod };
}

function methodLabel(method: MfaMethod): string {
  switch (method) {
    case 'totp':
      return 'Authenticator app code';
    case 'recovery_code':
      return 'Recovery code';
    case 'email':
      return 'Email code';
  }
}

export default function MfaChallengePage() {
  const [searchParams] = useSearchParams();
  const projectId = useMemo(() => searchParams.get('project_id') ?? '', [searchParams]);
  const mfaToken = useMemo(() => searchParams.get('mfa_token') ?? '', [searchParams]);
  const allowedMethodsParam = searchParams.get('methods');
  const allowedMethods: MfaMethod[] = useMemo(() => {
    const raw =
      allowedMethodsParam
        ?.split(',')
        .map(s => s.trim())
        .filter(Boolean) ?? [];
    const allowed = raw.filter(
      (m): m is MfaMethod => m === 'totp' || m === 'recovery_code' || m === 'email'
    );
    return allowed.length > 0 ? allowed : ['totp', 'recovery_code'];
  }, [allowedMethodsParam]);

  const [method, setMethod] = useState<MfaMethod>(allowedMethods[0]);
  const [code, setCode] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);
  const [remainingAttempts, setRemainingAttempts] = useState<number | null>(null);
  const [emailSentAt, setEmailSentAt] = useState<Date | null>(null);
  const [tokens, setTokens] = useState<SuccessTokens | null>(null);

  if (!projectId || !mfaToken) {
    return (
      <AuthLayout
        title="Invalid challenge link"
        subtitle="This sign-in link is missing required information. Please start over."
      >
        <p className="auth-help">
          <Link to="/login">Back to sign in</Link>
        </p>
      </AuthLayout>
    );
  }

  async function handleSendEmailCode() {
    setError(null);
    const result = await sendEmailMfaCode(projectId, mfaToken);
    if (result.success) {
      setEmailSentAt(new Date());
    } else {
      setError(result.error ?? 'Unable to send email code.');
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (!code.trim()) {
      setError('Enter your verification code.');
      return;
    }

    setStatus('submitting');
    const result = await verifyMfaChallenge(projectId, {
      mfa_token: mfaToken,
      code: code.trim(),
      method,
    });

    if (!result.success || !result.access_token) {
      setStatus('error');
      setRemainingAttempts(result.remaining_attempts ?? null);
      setError(result.error ?? 'Verification failed.');
      return;
    }

    // Hosted-login bridge: when /login forwarded the original flow params,
    // exchange the post-MFA bearer for a single-use authorization code and
    // redirect to the consumer's redirect_uri. The bearer never leaves
    // memory — we hand the code to the consumer instead.
    const flow = readHostedLoginFlow(searchParams);
    if (flow) {
      const codeResult = await authorizeCode(projectId, result.access_token, {
        redirect_uri: flow.redirectUri,
        state: flow.state,
        code_challenge: flow.codeChallenge,
        code_challenge_method: flow.codeChallengeMethod,
      });

      if (!codeResult.success || !codeResult.code) {
        // Don't redirect to a possibly-bad URI; surface the error in-page.
        setStatus('error');
        setError(
          codeResult.error_description ??
            codeResult.error ??
            'Could not complete sign-in. Please try again.'
        );
        return;
      }

      setStatus('success');
      const url = new URL(flow.redirectUri);
      url.searchParams.set('code', codeResult.code);
      url.searchParams.set('state', codeResult.state ?? flow.state);
      window.location.replace(url.toString());
      return;
    }

    // Direct/legacy use of /mfa/challenge without flow params: stop at the
    // success card (the consumer or an outer flow is responsible for what
    // happens with the issued tokens).
    setStatus('success');
    setTokens({
      access_token: result.access_token,
      refresh_token: result.refresh_token,
      expires_in: result.expires_in,
    });
  }

  if (status === 'success' && tokens) {
    return (
      <AuthLayout title="Signed in" subtitle="Verification succeeded. Returning you to the app…" />
    );
  }

  return (
    <AuthLayout
      title="Verify it’s you"
      subtitle="Enter the code from your second factor to finish signing in."
    >
      <form onSubmit={handleSubmit} className="auth-form" noValidate>
        {allowedMethods.length > 1 ? (
          <label className="auth-field">
            <span>Method</span>
            <select
              value={method}
              onChange={event => {
                setMethod(event.target.value as MfaMethod);
                setCode('');
                setError(null);
              }}
              disabled={status === 'submitting'}
            >
              {allowedMethods.map(m => (
                <option key={m} value={m}>
                  {methodLabel(m)}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {method === 'email' ? (
          <div className="auth-field">
            <button
              type="button"
              className="auth-secondary"
              onClick={handleSendEmailCode}
              disabled={status === 'submitting'}
            >
              {emailSentAt ? 'Resend email code' : 'Send email code'}
            </button>
            {emailSentAt ? (
              <p className="auth-help">
                Code sent at {emailSentAt.toLocaleTimeString()}. Check your inbox.
              </p>
            ) : null}
          </div>
        ) : null}

        <label className="auth-field">
          <span>{methodLabel(method)}</span>
          <input
            type="text"
            inputMode={method === 'recovery_code' ? 'text' : 'numeric'}
            autoComplete="one-time-code"
            autoFocus
            required
            value={code}
            onChange={event => setCode(event.target.value)}
            disabled={status === 'submitting'}
          />
        </label>

        {error ? (
          <p className="auth-error" role="alert">
            {error}
            {remainingAttempts !== null ? ` (${remainingAttempts} attempts remaining)` : ''}
          </p>
        ) : null}

        <button type="submit" className="auth-submit" disabled={status === 'submitting'}>
          {status === 'submitting' ? 'Verifying…' : 'Verify'}
        </button>
      </form>

      <p className="auth-help">
        <Link to="/login">Cancel and sign in again</Link>
      </p>
    </AuthLayout>
  );
}
